import React, { useEffect, useRef, useState } from "react";
import {
  getAthleteProfile,
  updateAthleteProfile,
  insertAthleteFileService,
  deleteAthleteFileService,
} from "../../../services/AthleteService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faEnvelope,
  faPenToSquare,
  faPhone,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { positions, states } from "../../../utils/PositionStateData";
import { faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import ConfirmModal from "../../../components/ConfirmModel";
import { useDispatch } from "react-redux";
import { setDisplayName } from "../../../store/slices/athleteSlice";

export const PersonalInfoAthlete = () => {
  const authUser = useAuthUser();
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    gpa: "",
    highSchool: "",
    city: "",
    state: "",
    primaryPosition: "",
    secondaryPosition: "",
    universityOfInterest: "",
    academicDegreeOfInterest: "",
    instagramHandle: "",
    twitterHandle: "",
    athleteEmail: "",
    parentEmail: "",
    athletePhone: "",
    parentPhone: "",
    transcriptFilePath: "",
    flxId: "",
    universitiesAttended: [],
  });

  const [isEditing, setIsEditing] = useState(false);
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const data = await getAthleteProfile(apiInstance, authUser?.user?.id);
        setProfileData({
          ...data,
          firstName: data?.firstName ?? data?.name ?? "",
          lastName: data?.lastName ?? data?.lastname ?? "",
          universitiesAttended: Array.isArray(data?.universitiesAttended)
            ? data.universitiesAttended
            : [],
        });
      } catch (error) {
        toast.error("Error fetching profile data");
      }
    };

    fetchProfileData();
  }, [authUser, apiInstance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleEditToggle = () => {
    setIsEditing((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const universityError = validateUniversitiesAttended(
      profileData.universitiesAttended,
    );
    if (universityError) {
      toast.error(universityError);
      return;
    }

    try {
      const updatedProfile = await updateAthleteProfile(
        apiInstance,
        authUser?.user?.id,
        profileData,
      );

      setProfileData(updatedProfile);
      dispatch(
        setDisplayName({
          firstName: updatedProfile?.firstName ?? profileData.firstName,
          lastName: updatedProfile?.lastName ?? profileData.lastName,
        }),
      );
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (error) {
      toast.error(
        "Error updating profile: " +
          (error.response?.data?.message || "Unknown error"),
      );
    }
  };

  const confirmDelete = async () => {
    const model = "AthleteFile";
    try {
      await deleteAthleteFileService(apiInstance, model, fileIdToDelete);
      toast.success("File deleted successfully!");
      setProfileData((prevState) => ({
        ...prevState,
        transcriptFilePath: "",
      }));
    } catch (error) {
      toast.error("Error to delete: " + error.response?.data?.message);
    } finally {
      setShowConfirmModal(false);
      setFileIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setFileIdToDelete(null);
  };

  const handleUniversityChange = (index, field, value) => {
    const updated = [...profileData.universitiesAttended];
    updated[index][field] = value;

    setProfileData({
      ...profileData,
      universitiesAttended: updated,
    });
  };

  const addUniversity = () => {
    const last =
      profileData.universitiesAttended[
        profileData.universitiesAttended.length - 1
      ];

    if (last && (!last.school || !last.year || !last.className)) {
      toast.error(
        "Please fill all fields of the current university before adding another.",
      );
      return;
    }

    setProfileData({
      ...profileData,
      universitiesAttended: [
        ...profileData.universitiesAttended,
        { school: "", year: 0, className: "" },
      ],
    });
  };

  const removeUniversity = (index) => {
    const updated = profileData.universitiesAttended.filter(
      (_, i) => i !== index,
    );

    setProfileData({
      ...profileData,
      universitiesAttended: updated,
    });
  };

  const validateUniversitiesAttended = (universities) => {
    if (!Array.isArray(universities) || universities.length === 0) return null;

    for (let i = 0; i < universities.length; i++) {
      const { school, year, className } = universities[i];

      if (!school || school.trim() === "") {
        return `University name is required (Row ${i + 1}).`;
      }
      if (!year || Number(year) <= 0) {
        return `Valid year is required (Row ${i + 1}).`;
      }
      if (!className || className.trim() === "") {
        return `Class name is required (Row ${i + 1}).`;
      }
    }

    return null;
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <ConfirmModal
        show={showConfirmModal}
        title="Delete Transcript?"
        message="Are you sure you want to delete this file?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      <div className="w-full">
        <div
          className="float-right text-gray-600 border border-gray-300 px-3 py-1 rounded bg-gray-300 cursor-pointer"
          onClick={handleEditToggle}
        >
          {isEditing ? "Cancel" : "Edit"}
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="h-4 text-gray-600 ml-2"
          />
        </div>
        <h2 className="text-xl mb-4 uppercase">Personal Info</h2>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="flex flex-wrap -mx-3">
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="firstName"
              >
                First Name
              </label>
              <div className="relative">
                <input
                  name="firstName"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="firstName"
                  type="text"
                  value={profileData.firstName ?? profileData.name ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="lastName"
              >
                Last Name
              </label>
              <div className="relative">
                <input
                  name="lastName"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="lastName"
                  type="text"
                  value={profileData.lastName ?? profileData.lastname ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="flxId"
              >
                Flx Id
              </label>
              <div className="relative">
                <input
                  name="flxId"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="flxId"
                  type="text"
                  value={profileData.flxId ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="primaryPosition"
              >
                Position
              </label>
              <div className="relative">
                <select
                  name="primaryPosition"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="primaryPosition"
                  value={profileData.primaryPosition || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="w-full md:w-2/4 px-3 mb-6 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="secondaryPosition"
              >
                Secondary Position
              </label>
              <div className="relative">
                <select
                  name="secondaryPosition"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="secondaryPosition"
                  value={profileData.secondaryPosition || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="w-full md:w-1/2 px-3 pt-2">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="highSchool"
              >
                High School You Graduated From
              </label>
              <input
                name="highSchool"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="highSchool"
                type="text"
                value={profileData.highSchool ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>

            <div className="w-full md:w-1/2 px-3 pt-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="city"
              >
                Hometown City
              </label>
              <input
                name="city"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="city"
                type="text"
                value={profileData.city ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 pt-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="state"
              >
                Hometown State
              </label>
              <div className="relative">
                <select
                  name="state"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="state"
                  value={profileData.state ?? ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 pt-2">
            <div className="w-full md:w-1/2 px-3 ">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="universityOfInterest"
              >
                Universities you are Most Interested in Playing for
              </label>
              <input
                name="universityOfInterest"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="universityOfInterest"
                type="text"
                value={profileData.universityOfInterest ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="academicDegreeOfInterest"
              >
                Academic Degree of Interest
              </label>
              <input
                name="academicDegreeOfInterest"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="academicDegreeOfInterest"
                type="text"
                value={profileData.academicDegreeOfInterest ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="instagram"
              >
                Instagram
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faInstagram}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="instagram"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="instagram"
                  type="text"
                  value={profileData.instagram ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="twitter"
              >
                Twitter
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faXTwitter}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="twitter"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="twitter"
                  type="text"
                  value={profileData.twitter ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="athleteEmail"
              >
                Athlete Email
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="athleteEmail"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="athleteEmail"
                  type="email"
                  value={profileData.athleteEmail ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="parentEmail"
              >
                Parent Email
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="parentEmail"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="parentEmail"
                  type="email"
                  value={profileData.parentEmail ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="athletePhone"
              >
                Athlete Phone
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />

                <input
                  name="athletePhone"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="athletePhone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={profileData.athletePhone ?? ""}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: numericValue,
                      },
                    });
                  }}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="parentPhone"
              >
                Parent Phone
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="parentPhone"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="parentPhone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={profileData.parentPhone ?? ""}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: numericValue,
                      },
                    });
                  }}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="w-full mb-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              {/* GPA */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl text-gray-300 opacity-80 transition-opacity duration-200">
                    🎓
                  </span>
                  <label
                    htmlFor="gpa"
                    className="uppercase tracking-wide text-gray-500 text-xs font-bold"
                  >
                    GPA
                  </label>
                </div>
                <input
                  name="gpa"
                  id="gpa"
                  type="text"
                  value={profileData.gpa ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  placeholder="Enter GPA"
                />
              </div>

              {/* Transcript */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📄</span>
                  <span className="uppercase tracking-wide text-gray-500 text-xs font-bold">
                    Transcript
                  </span>
                </div>
                <TranscriptUploader
                  transcriptFilePath={profileData.transcriptFilePath}
                  transcriptFileId={profileData.transcriptFileId}
                  onUpdateFilePath={(url) =>
                    setProfileData((prev) => ({
                      ...prev,
                      transcriptFilePath: url,
                    }))
                  }
                  onUpdateFileId={(id) =>
                    setProfileData((prev) => ({
                      ...prev,
                      transcriptFileId: id,
                    }))
                  }
                  apiInstance={apiInstance}
                />
              </div>
            </div>
          </div>

          {/* Universities Attended */}
          <div className="w-full px-3 mt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold uppercase text-gray-600">
                Universities Attended
              </h3>
              {isEditing && (
                <button
                  type="button"
                  onClick={addUniversity}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add University
                </button>
              )}
            </div>

            {profileData.universitiesAttended.map((uni, index) => (
              <div
                key={index}
                className="flex flex-wrap items-center gap-2 mb-3"
              >
                {isEditing ? (
                  <input
                    type="text"
                    placeholder="University Name"
                    value={uni.school}
                    onChange={(e) =>
                      handleUniversityChange(index, "school", e.target.value)
                    }
                    className="flex-1 border border-gray-300 rounded p-2 text-sm"
                  />
                ) : (
                  <div>
                    {" "}
                    <span>{uni.school} </span>
                  </div>
                )}

                {isEditing ? (
                  <input
                    type="number"
                    placeholder="Year"
                    value={uni.year}
                    onChange={(e) =>
                      handleUniversityChange(
                        index,
                        "year",
                        Number(e.target.value),
                      )
                    }
                    className="w-28 border border-gray-300 rounded p-2 text-sm"
                  />
                ) : (
                  <div>
                    {" "}
                    <span>{uni.year} </span>
                  </div>
                )}

                {isEditing ? (
                  <input
                    type="text"
                    placeholder="Class (ex: Freshman)"
                    value={uni.className}
                    onChange={(e) =>
                      handleUniversityChange(index, "className", e.target.value)
                    }
                    className="w-40 border border-gray-300 rounded p-2 text-sm"
                  />
                ) : (
                  <div>
                    {" "}
                    <span>{uni.className} </span>
                  </div>
                )}

                {profileData.universitiesAttended.length > 1 && isEditing && (
                  <button
                    type="button"
                    onClick={() => removeUniversity(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {isEditing && (
            <div className="flex justify-center mt-2">
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const TranscriptUploader = ({
  transcriptFilePath,
  transcriptFileId,
  onUpdateFilePath,
  onUpdateFileId,
  apiInstance,
}) => {
  const fileInputRef = useRef(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await insertAthleteFileService(
        apiInstance,
        "Transcript",
        file,
      );
      const { fileUrl, uploadedId } = response.data;

      onUpdateFilePath(fileUrl);
      onUpdateFileId(uploadedId);

      toast.success("File uploaded successfully!");
    } catch (error) {
      toast.error(
        "Error uploading file: " + (error.response?.data?.message || "Error"),
      );
    }
  };

  const handleFileDelete = () => {
    setFileIdToDelete(transcriptFileId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteAthleteFileService(
        apiInstance,
        "AthleteFile",
        fileIdToDelete,
      );
      toast.success("File deleted successfully!");
      onUpdateFilePath("");
      onUpdateFileId(null);
    } catch (error) {
      toast.error("Error deleting file: " + error.response?.data?.message);
    } finally {
      setShowConfirmModal(false);
      setFileIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setFileIdToDelete(null);
  };

  return (
    <div className="w-full px-3">
      <ConfirmModal
        show={showConfirmModal}
        title="Delete Transcript?"
        message="Are you sure you want to delete this file?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        contentClassName="max-w-lg"
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleFileSelect}
          className="bg-blue-600 text-white px-4 py-2 rounded cursor-pointer flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-lg" />
          <span className="text-sm">Click to upload file</span>
        </button>

        <input
          type="file"
          accept=".pdf,.jpg,.jpeg"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {transcriptFilePath && (
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
            <a
              href={transcriptFilePath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 text-sm font-medium truncate max-w-[200px] hover:underline"
            >
              {transcriptFilePath.split("/").pop()}
            </a>
            <button
              type="button"
              onClick={handleFileDelete}
              className="text-red-600 hover:text-red-800"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptUploader;
